import { Sparkles } from 'lucide-react';

import type { AppPhase, ProcessingStep } from '../../stores/useAppStore';
import { Card, CardContent } from '../ui/card';
import { Progress } from '../ui/progress';

const STEP_LABELS: Record<ProcessingStep, string> = {
  classifying: 'カテゴリ判別中',
  'processing-images': '画像処理中',
  'removing-reflections': '看板の映り込みを除去中',
  'composing-hero': 'ヒーロー写真を合成中',
  'building-zip': 'ZIP を作成中',
};

const STEP_ORDER: readonly ProcessingStep[] = [
  'classifying',
  'processing-images',
  'removing-reflections',
  'composing-hero',
  'building-zip',
];

export interface ProgressViewProps {
  readonly phase: Extract<AppPhase, { kind: 'processing' }>;
}

export function ProgressView(props: ProgressViewProps): JSX.Element {
  const { phase } = props;
  const percent = phase.total > 0 ? Math.round((phase.current / phase.total) * 100) : 0;

  return (
    <Card className="mx-auto max-w-xl">
      <CardContent className="space-y-6 p-8">
        <div className="flex items-center gap-3">
          <Sparkles
            className="text-accent-primary h-5 w-5 animate-pulse"
            strokeWidth={1.5}
            aria-hidden
          />
          <h2 className="text-text-primary text-base font-semibold">処理中…</h2>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-text-primary text-sm">{STEP_LABELS[phase.step]}</span>
            <span className="text-text-secondary text-sm tabular-nums">
              <span className="text-text-primary">{phase.current}</span>
              <span className="mx-1">/</span>
              <span>{phase.total}</span>
            </span>
          </div>
          <Progress value={percent} />
        </div>

        <StepIndicator currentStep={phase.step} />

        <p className="text-text-tertiary text-xs">
          ブラウザのタブを閉じないでください。処理中の画像データは保持されません。
        </p>
      </CardContent>
    </Card>
  );
}

// ===== サブコンポーネント ================================================

function StepIndicator(props: { readonly currentStep: ProcessingStep }): JSX.Element {
  const currentIdx = STEP_ORDER.indexOf(props.currentStep);
  return (
    <ol className="border-silver-tertiary space-y-2 border-t pt-4">
      {STEP_ORDER.map((step, i) => {
        const completed = i < currentIdx;
        const active = i === currentIdx;
        return (
          <li key={step} className="flex items-center gap-3">
            <span
              className={
                completed
                  ? 'bg-status-success flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white'
                  : active
                    ? 'bg-accent-primary flex h-5 w-5 animate-pulse items-center justify-center rounded-full text-[10px] font-bold text-white'
                    : 'bg-silver-tertiary text-text-secondary flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-medium'
              }
            >
              {completed ? '✓' : i + 1}
            </span>
            <span
              className={
                active
                  ? 'text-text-primary text-sm font-medium'
                  : completed
                    ? 'text-text-secondary text-sm line-through'
                    : 'text-text-tertiary text-sm'
              }
            >
              {STEP_LABELS[step]}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
